<script lang="ts" setup>
import useBreakpoints from '@exchange/libs/composables/shared/src/lib/useBreakpoints';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';

import OpenOrdersListDesktop from './desktop/OpenOrdersListDesktop.vue';
import OpenOrdersListMobile from './mobile/OpenOrdersListMobile.vue';

defineProps({
  authenticatedUser: { type: Boolean, required: true },
  dragging: { type: Boolean, required: true },
  marketId: { type: String, required: true },
  showAllMarkets: { type: Boolean, required: true },
});

const breakpoints = {
  780: 'hide-type compact-side hide-currency compact-status', // 'compact-side compact-order-type hide-time hide-currency compact-status compact-order-type',
  850: 'hide-type compact-side', // 'compact-side compact-order-type hide-time compact-order-type',
  880: 'hide-type compact-side', // 'compact-side compact-order-type compact-order-type',
  920: 'hide-type compact-side', // 'compact-side compact-order-type',
  bigger: 'hide-type',
};

/* eslint-disable compat/compat */
// @ts-ignore Wait for a proper Houdini API support
const houdiniApiSupported = !!(window.CSS && window.CSS.registerProperty);
const { currentBreakPoint, watchedEl: wrapperRef } = useBreakpoints(breakpoints);
const { isXS } = useMQBreakpoints();
</script>

<template>
  <div
    ref="wrapperRef"
    class="open-orders"
    :class="{ 'open-orders--houdini-api-not-supported': !houdiniApiSupported }"
  >
    <open-orders-list-desktop
      v-if="!isXS"
      class="open-orders__list"
      :show-order-type="false"
      :breakpoint="currentBreakPoint"
      :dragging="dragging"
      :market-id="marketId"
      :show-all-markets="showAllMarkets"
      :authenticated-user="authenticatedUser"
    />
    <open-orders-list-mobile
      v-else
      class="open-orders__list"
      :show-order-type="false"
      :market-id="marketId"
      :show-all-markets="showAllMarkets"
      :authenticated-user="authenticatedUser"
    />
  </div>
</template>

<style lang="scss">
/* stylelint-disable custom-property-no-missing-var-function */

/** TODO fix styles and enable stylelint */
@import '~@exchange/styles/mixins';

/* stylelint-disable */
/* eslint-disable */
/* prettier-ignore */
$classes: --side-color, --status-color, --side-icon-color, --order-side-color, --market-id-color, --market-id-separator-color, --titles-color, --advanced-type-color,
  --number-amount-color, --number-price-color, --number-total-color, --number-stop-price-color, --order-time-color;

@include houdini-register-properties($classes);

.open-orders {
  /** PROFE-110 height: 100%; */
  display: flex;
  flex-direction: column;

  %set-current-color {
    /* these variables always have the same color at the one point of time on desktop
     that is why we don't have other variables here, as they can variate depends of order status */
    --titles-color: currentcolor;
    --side-icon-color: currentcolor;
    --order-side-color: currentcolor;
    --advanced-type-color: currentcolor;
    --market-id-color: currentcolor;
    --number-amount-color: currentcolor;
    --number-price-color: currentcolor;
    --number-total-color: currentcolor;
    --order-time-color: currentcolor;
  }

  %set-current-color-success-and-fail {
    /** Safari freaks out if the following variables are just placed in the animation keyframes */

    /* variables below can't be listed in %set-current-color as in some cases of using this extending
some variables should not have currentcolor and there is no way to define it twice */
    --market-id-separator-color: currentcolor;
    --number-stop-price-color: currentcolor;
    --status-color: rgb(var(--v-theme-text-primary));
  }

  /* - Animations: */

  @keyframes success {
    0% {
      @extend %set-current-color;
      @extend %set-current-color-success-and-fail;

      background-color: rgb(var(--v-theme-success));
      color: rgb(var(--v-theme-text-primary));
    }
  }

  @keyframes fail {
    0% {
      @extend %set-current-color;
      @extend %set-current-color-success-and-fail;

      background-color: rgb(var(--v-theme-error));
      color: rgb(var(--v-theme-text-primary));
    }
  }

  &__list {
    tr {
      border-left: 3px solid var(--status-border-color, transparent);

      /* needed for suspended and maintenance statuses */
    }

    /* Default colors */
    --status-border-color: transparent;
    --advanced-type-color: rgb(var(--v-theme-text-secondary));

    .xs & {
      --titles-color: rgb(var(--v-theme-text-tertiary));
      --number-amount-color: rgb(var(--v-theme-text-primary));
      --number-price-color: rgb(var(--v-theme-text-primary));
      --number-total-color: rgb(var(--v-theme-text-secondary));
      --market-id-color: rgb(var(--v-theme-text-primary));
      --order-time-color: rgb(var(--v-theme-text-primary));
    }

    /* Side colors */
    .buy {
      --side-color: rgb(var(--v-theme-success));
    }

    .sell {
      --side-color: rgb(var(--v-theme-error));
    }

    /* Status colors */
    .status-open {
      --side-icon-color: var(--side-color);
      --order-side-color: var(--side-color);
      --number-stop-price-color: var(--side-color);
      --status-color: rgb(var(--v-theme-primary));
    }

    .status-triggered {
      --side-icon-color: var(--side-color);
      --order-side-color: var(--side-color);
      --number-stop-price-color: rgb(var(--v-theme-text-secondary));
      --status-color: rgb(var(--v-theme-primary));
    }

    .status-filled {
      --side-icon-color: var(--side-color);
      --order-side-color: var(--side-color);
      --number-stop-price-color: rgb(var(--v-theme-text-secondary));
      --status-color: rgb(var(--v-theme-text-primary));
    }

    .market-suspended,
    .market-in-maintenance {
      @extend %set-current-color;

      --market-id-separator-color: currentcolor;
      --number-stop-price-color: currentcolor;
      --status-color: rgb(var(--v-theme-warning));
      --status-border-color: rgb(var(--v-theme-warning));

      background-color: rgb(var(--v-theme-elevation-0));
      color: rgb(var(--v-theme-text-tertiary));
    }

    .status-filled-rejected,
    .status-rejected,
    .status-failed {
      @extend %set-current-color;

      --market-id-separator-color: currentcolor;
      --number-stop-price-color: var(currentColor);
      --status-color: rgb(var(--v-theme-error));

      background-color: rgb(var(--v-theme-elevation-0));
      color: rgb(var(--v-theme-text-tertiary));
    }

    .status-filled-fully,
    .status-filled-closed {
      @extend %set-current-color;

      --market-id-separator-color: currentcolor;
      --number-stop-price-color: currentcolor;
      --status-color: currentcolor;

      background-color: rgb(var(--v-theme-elevation-0));
      color: rgb(var(--v-theme-text-tertiary));
    }

    .status-closing,
    .status-closed {
      @extend %set-current-color;

      --number-stop-price-color: currentcolor;
      --market-id-separator-color: currentcolor;

      background-color: rgb(var(--v-theme-elevation-0));
      color: rgb(var(--v-theme-text-tertiary));
    }
  }
}

/* remove it when ff and safari support houdini API */
.open-orders--houdini-api-not-supported {
  /* Side colors */
  .buy {
    .order-side-icon,
    .order-side {
      color: rgb(var(--v-theme-success));
    }
  }

  .sell {
    .order-side-icon,
    .order-side {
      color: rgb(var(--v-theme-error));
    }
  }

  /* Status colors */
  .status-open,
  .status-triggered {
    .order-status {
      color: rgb(var(--v-theme-primary));
    }
  }

  /* Gray Trigger Price when order has been triggered */
  .status-filled,
  .status-triggered,
  .status-closing {
    .order-stop-price__value {
      color: rgb(var(--v-theme-text-secondary));
    }
  }

  .market-suspended,
  .market-in-maintenance {
    .order-status {
      color: rgb(var(--v-theme-warning));
    }

    .order-side,
    .order-side-icon,
    .order-stop-price__value {
      color: currentcolor;
    }

    tr {
      border-color: rgb(var(--v-theme-warning));
    }
  }

  .status-filled-fully,
  .status-filled-rejected,
  .status-filled-closed,
  .status-closing,
  .status-closed,
  .status-rejected,
  .status-failed {
    background-color: rgb(var(--v-theme-elevation-0));
    color: rgb(var(--v-theme-text-tertiary));

    .order-status,
    .order-side-icon,
    .order-side,
    .order-amount__value,
    .order-price__value,
    .order-total__value,
    .order-stop-price__value,
    .order-display-type--advanced-type,
    .x-market-id__separator {
      color: currentcolor;
    }
  }

  .status-filled-rejected,
  .status-rejected,
  .status-failed {
    .order-status {
      color: rgb(var(--v-theme-error));
    }
  }

  /* We need it to force text color changing on animation  */
  .animation-running {
    .order-side-icon,
    .order-side,
    .x-market-id__separator,
    .order-status,
    .order-stop-price__value {
      animation-duration: 1s;
      animation-name: no-houdini-animation;
      animation-timing-function: ease-in;
    }
  }

  @keyframes no-houdini-animation {
    0% {
      color: rgb(var(--v-theme-text-primary));
    }
  }
}

/* stylelint-enable */
</style>
